// your-tooltip.jsx
import React from 'react'
import styled, {css} from 'styled-components'
import Tooltip, {TooltipProps, Placement, TooltipContentProps} from '@fuegokit/react/components/Tooltip'
import {Merge} from '@fuegokit/react/utils/types'
import {themeGet} from '@fuegokit/react/utils/theme-get'
import sx, {SxProp} from '@fuegokit/react/sx'

// Root
interface OwnProps extends TooltipProps {
  /**
   * The content of the tooltip. It can be either a:
   * 1. `ReactNode`
   * 2. Function which returns a `ReactNode`

   * The benefit of the second approach is that it allows you to consume the `update` render prop.
   * This `update` function can be called to manually recalculate the position of the tooltip.
   */
  content?: React.ReactNode | (({update}: {update: () => void}) => React.ReactNode)
  /**
   * Whether to hide an optional arrow
   */
  hideArrow?: boolean
  /**
   * Show only one line of text, and truncate when too long
   */
  truncate?: boolean
  /**
   * Where the tooltip should appear relative to its target.
   */
  position?: Placement
  /**
   * Time in milliseconds to wait before showing and hiding the tooltip. Defaults to 300.
   */
  delay?: number
}

type InternalAtlasTooltipProps = Merge<OwnProps, TooltipContentProps>

const renderTruncateStyles = (truncate: boolean) => {
  if (truncate) {
    return css`
      max-width: 420px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `
  }
  return null
}

const StyledTooltipContent = styled(Tooltip.Content)<InternalAtlasTooltipProps & SxProp>`
  box-sizing: border-box;
  max-width: 240px;
  padding: 2px 6px;
  top: 0;
  left: 0;
  border-radius: ${themeGet('radii.1')};
  font-size: ${themeGet('fontSizes.2')};
  line-height: 1.3;
  overflow-wrap: break-word;
  word-wrap: break-word;

  background-color: ${themeGet('colors.background.neutral.bold.default')};
  color: ${themeGet('colors.text.inverse')};
  // truncate styles
  ${p => renderTruncateStyles(Boolean(p.truncate))}
  ${sx}
`
const StyledTooltipArrow = styled(Tooltip.Arrow)<InternalAtlasTooltipProps & SxProp>`
  fill: ${themeGet('colors.background.neutral.bold.default')};
  ${sx}
`

/**
 * Accessible tooltip that can be used as a 1:1 drop-in replacement in Atlassian products.
 *
 * Provides additional functionality like an optional arrow and customizable display timing.
 *
 * To be rendered inside of a <Tooltip.Provider>. To render a customized component, extend the Fuegokit React Tooltip primitive to create your own tooltip.
 *
 * See https://fuegokit.design/react/primitives/tooltip
 */
const AtlasTooltip = ({
  children,
  content,
  open,
  defaultOpen,
  position,
  delay = 300,
  onOpenChange,
  hideArrow = true,
  align,
  ...props
}: InternalAtlasTooltipProps) => {
  return (
    <Tooltip open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} delayDuration={delay}>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      {/* @TODO fix side in Tooltip; we need to override Tooltip's default `side` prop for extended placement options */}
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <StyledTooltipContent side={position as any} align={align} {...props}>
        {content}
        {!hideArrow && <StyledTooltipArrow width={11} height={5} />}
      </StyledTooltipContent>
    </Tooltip>
  )
}

export type AtlasTooltipProps = InternalAtlasTooltipProps
AtlasTooltip.displayName = 'AtlasTooltip'
export default AtlasTooltip
